// Libraries
import { Card, Flex, Skeleton, Text } from '@chakra-ui/react';
// Components
import { RoundedArrowIcon, TextWithTooltip } from '@atoms/index';
// Utils
import { formatPercentage, parseToThousands } from '@helpers/index';
import text from '@translations/en.json';
// Types
import { UwcCardProps } from './UwcCard.types';

/**
 * A card component used to display information about underwater cleaning (UWC) changes,
 * including the percentage change, fuel oil consumption (FOC) change, and CO2 change.
 *
 * @component
 * @param {UwcCardProps} props - The props for the UwcCard component.
 * @param {boolean} props.isLoading - Optional flag indicating whether the component is loaded.
 *
 * @returns {ReactElement} The UwcCard component.
 */
export const UwcCard = ({ isLoading = false, uwc }: UwcCardProps) => {
  const { percentageChange, focChange, co2Change } = uwc ?? {};
  const positive = { color: 'error.base', transform: 'rotate(-90deg)' };
  const negative = { color: 'base', transform: 'rotate(90deg)' };
  const arrowStyles = percentageChange && percentageChange > 0 ? positive : negative;
  const isPercentageChangeLoading = !isLoading && percentageChange !== undefined;
  const isFocChangeLoading = !isLoading && focChange !== undefined;
  const isCo2ChangeLoading = !isLoading && co2Change !== undefined;
  const focText = `${text.foc}: ${parseToThousands(focChange, 1)} ${text.mt}`;
  const co2Text = `: ${parseToThousands(co2Change, 1)} ${text.mt}`;

  return (
    <Card w='full' gap={4} px={{ base: 4, md: 6 }} py={{ base: 4, md: 5 }}>
      <TextWithTooltip
        tooltipText={text.cii_simulator.uwc_card.title_tooltip}
        fontSize={{ base: 'md', md: 'xl' }}
        fontWeight='medium'
      >
        {text.cii_simulator.uwc_card.title}
      </TextWithTooltip>
      <Flex flex={1} align='center' gap={4}>
        <Skeleton isLoaded={isPercentageChangeLoading} flex='1'>
          <Flex align='center'>
            <RoundedArrowIcon boxSize='7' {...arrowStyles} />
            <Text color='grey.800' fontSize='hl' whiteSpace='nowrap'>
              {formatPercentage(percentageChange ?? 0, 1)}
            </Text>
          </Flex>
        </Skeleton>
        <Flex direction='column' gap={3} flex='1'>
          <Skeleton isLoaded={isFocChangeLoading}>
            <Text fontSize='l'>{focText}</Text>
          </Skeleton>
          <Skeleton isLoaded={isCo2ChangeLoading}>
            <Text fontSize='l' whiteSpace='nowrap'>
              {text.co}
              <Text fontSize='s' as='sub'>
                2
              </Text>
              {co2Text}
            </Text>
          </Skeleton>
        </Flex>
      </Flex>
    </Card>
  );
};
